import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, formatApiError } from "@/lib/api";
import { LinkRow } from "./LinkRow";

const EMPTY = { code: "", advisor_name: "", email: "", mobile: "", label: "" };

export function LinksManager({ onChanged }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [copied, setCopied] = useState("");
  const [filter, setFilter] = useState("");

  const links = useQuery({ queryKey: ["admin-links"], queryFn: async () => (await adminApi.get("/admin/links")).data });
  const base = (links.data?.site_url || window.location.origin).replace(/\/$/, "");
  const buildUrl = (code) => `${base}/?ref=${code}`;

  const visible = useMemo(() => {
    const items = links.data?.items ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((l) => [l.code, l.advisor_name, l.email, l.mobile, l.label].some((v) => (v || "").toLowerCase().includes(q)));
  }, [links.data, filter]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-links"] });
    onChanged?.();
  };
  const create = useMutation({
    mutationFn: (body) => adminApi.post("/admin/links", body),
    onSuccess: () => { toast.success("Referral link created"); setForm(EMPTY); invalidate(); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => adminApi.patch(`/admin/links/${id}`, body),
    onSuccess: (_, { body }) => {
      if (body.email !== undefined) toast.success("Adviser email updated");
      if (body.mobile !== undefined) toast.success(body.mobile ? "Adviser WhatsApp mobile updated" : "Adviser mobile cleared");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: (id) => adminApi.delete(`/admin/links/${id}`),
    onSuccess: () => { toast.success("Link deleted"); invalidate(); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const writeClipboard = async (text, okMessage) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement("textarea");
        area.value = text;
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        area.remove();
        if (!ok) throw new Error("execCommand failed");
      }
      toast.success(okMessage);
      return true;
    } catch (_) {
      toast.error("Copy failed. Select the URL manually");
      return false;
    }
  };
  const copy = async (code) => {
    if (await writeClipboard(buildUrl(code), "Link copied")) {
      setCopied(code);
      setTimeout(() => setCopied(""), 1600);
    }
  };
  const copyAll = () => {
    const lines = visible.filter((l) => l.active).map((l) => `${l.advisor_name} (${l.code}): ${buildUrl(l.code)}`);
    writeClipboard(lines.join("\n"), `${lines.length} links copied`);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    create.mutate({ code: form.code.trim().toUpperCase(), advisor_name: form.advisor_name.trim(), email: form.email.trim(), mobile: form.mobile.trim(), label: form.label.trim() });
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
      <form onSubmit={onSubmit} className="admin-card h-fit p-6" data-testid="create-link-form">
        <p className="admin-kicker mb-2">New referral link</p>
        <h2 className="mb-5 text-xl font-bold tracking-tight">Give each adviser a unique link</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="linkCode">Code <span className="text-white/40">(IFA code)</span></Label>
            <Input id="linkCode" required minLength={2} maxLength={24} pattern="[A-Za-z0-9_\-]+" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. IFA0319" className="h-11 rounded-xl border-white/15 bg-white/5 uppercase" data-testid="link-code-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkAdvisor">Adviser name</Label>
            <Input id="linkAdvisor" required minLength={2} value={form.advisor_name} onChange={(e) => setForm({ ...form, advisor_name: e.target.value })} placeholder="Nicholas Lee" className="h-11 rounded-xl border-white/15 bg-white/5" data-testid="link-advisor-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkEmail">Adviser email <span className="text-white/40">(gets notified on each registration)</span></Label>
            <Input id="linkEmail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nicholas.lee@infinityfa.com.sg" className="h-11 rounded-xl border-white/15 bg-white/5" data-testid="link-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkMobile">Adviser WhatsApp mobile <span className="text-white/40">(for the follow-up "Book a chat" button)</span></Label>
            <Input id="linkMobile" type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+65 9123 4567" className="h-11 rounded-xl border-white/15 bg-white/5" data-testid="link-mobile-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkLabel">Label <span className="text-white/40">(optional)</span></Label>
            <Input id="linkLabel" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Facebook ad · Sep cohort" className="h-11 rounded-xl border-white/15 bg-white/5" data-testid="link-label-input" />
          </div>
          <Button type="submit" disabled={create.isPending} className="admin-gold-btn h-11 w-full rounded-full text-xs font-bold" data-testid="create-link-btn">
            <Link2 className="mr-1.5 h-3.5 w-3.5" /> {create.isPending ? "Creating…" : "Create link"}
          </Button>
          {form.code && (
            <p className="break-all rounded-lg bg-white/5 px-3 py-2 text-xs text-white/60" data-testid="link-preview">{buildUrl(form.code)}</p>
          )}
        </div>
      </form>

      <div className="admin-card p-5 sm:p-6" data-testid="links-panel">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="admin-kicker" data-testid="links-count">Referral links · {links.data?.items.length ?? 0}</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search adviser, code or email" className="h-9 w-60 rounded-full border-white/15 bg-white/5 pl-9 text-xs" data-testid="links-search-input" />
            </div>
            <Button size="sm" variant="outline" onClick={copyAll} disabled={visible.length === 0} className="h-9 rounded-full border-white/15 bg-white/5 text-xs font-semibold hover:bg-white/10" data-testid="copy-all-links-btn">
              <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Copy all links
            </Button>
          </div>
        </div>
        <Table data-testid="links-table">
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/50">Code</TableHead>
              <TableHead className="text-white/50">Adviser · email · WhatsApp mobile</TableHead>
              <TableHead className="text-white/50">Link</TableHead>
              <TableHead className="text-right text-white/50">Registrations</TableHead>
              <TableHead className="text-white/50">Active</TableHead>
              <TableHead className="text-right text-white/50">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.data && visible.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-white/50" data-testid="links-empty">{filter ? "No links match your search." : "No referral links yet. Create the first one on the left."}</TableCell></TableRow>
            )}
            {visible.map((link) => (
              <LinkRow
                key={link.id}
                link={link}
                url={buildUrl(link.code)}
                copied={copied === link.code}
                onCopy={() => copy(link.code)}
                onUpdate={(body) => update.mutate({ id: link.id, body })}
                onDelete={() => { if (window.confirm(`Delete link ${link.code}? Existing registrations keep the code.`)) remove.mutate(link.id); }}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
